// Buddy: chat (odpověď streamovaná přes SSE), čekající návrhy a spotřeba AI.
// Historie konverzace žije jen v paměti (po obnovení stránky se začíná znovu).
import { defineStore } from 'pinia'
import { ref } from 'vue'
import {
  CHAT_ASSISTANT_MAX_LENGTH,
  CHAT_MAX_MESSAGES,
  type AiProposalItem,
  type AiUsageResponse,
  type ChatEvent,
  type ChatMessage,
  type ChatToolName,
  type ProposalNeedsInputDetails,
} from '@wac-buddy/shared'
import {
  approveProposal,
  getAiNote,
  getAiProposals,
  getAiUsage,
  rejectProposal,
  undoProposal,
} from '@/api/ai'
import { ApiClientError } from '@/api/client'
import { conflictCandidates, type ClientCandidate } from '@/api/clients'
import { streamSse } from '@/lib/sse'
import { useCalendarStore } from './calendar'
import { useClientsStore } from './clients'
import { useJobsStore } from './jobs'
import { usePromisesStore } from './promises'
import { useTasksStore } from './tasks'
import { useTodayStore } from './today'

export type BuddyStatus = 'idle' | 'loading' | 'ready' | 'error'

/** Jak často se ptáme na stav hlasovky a jak dlouho (60 s). */
export const VOICE_POLL_MS = 1500
export const VOICE_POLL_MAX_TICKS = 40
/** Jak dlouho se po stavu `failed` ještě sleduje (fronta může úlohu zopakovat), než se ukáže chyba. */
export const VOICE_FAILED_GRACE_MS = 20_000

/** Kolik rozhodnutých návrhů z této relace se pamatuje (kvůli „Vrátit zpět“). */
export const RECENT_DECIDED_MAX = 20

export type ProposalActionResult =
  | { ok: true; proposal: AiProposalItem }
  | {
      ok: false
      errorKey: string
      needsInput?: ProposalNeedsInputDetails
      /** Schválení klienta narazilo na podobného (409 s kandidáty): formulář nabídne „založit přesto“. */
      duplicate?: ClientCandidate[]
    }

function isNeedsInput(details: unknown): details is ProposalNeedsInputDetails {
  return (
    typeof details === 'object' &&
    details !== null &&
    (details as { needsInput?: unknown }).needsInput === true
  )
}

/** Údaje hlasovky v uživatelské bublině. */
export interface ChatVoiceInfo {
  noteId: string
  interactionId: string
  durationMs: number
  /** Podepsaná adresa k přehrání, až ji server vrátí. */
  recordingUrl: string | null
  /** true = text v bublině je přepis ze serveru (jinak text ze zařízení nebo nic). */
  transcribed: boolean
}

export interface AttachVoiceNoteInput {
  noteId: string
  interactionId: string
  durationMs: number
  deviceText: string
}

export interface ChatToolChip {
  name: ChatToolName
  status: 'running' | 'done' | 'failed'
}

export interface ChatUiMessage {
  id: string
  role: 'user' | 'assistant'
  text: string
  tools: ChatToolChip[]
  proposals: AiProposalItem[]
  streaming: boolean
  /** i18n klíč (buddy.errors.*, common.network) nebo hotová hláška ze serveru. */
  error?: string
  /** Uživatelská bublina hlasovky (text = přepis nebo text ze zařízení). */
  voice?: ChatVoiceInfo
  /** Odpověď na hlasovku: interactionId hlasovky, na kterou odpovídá. */
  voiceFor?: string
  /** Stav zpracování hlasovky (i18n klíč buddy.voice.*) a počet pro množné číslo. */
  statusKey?: string
  statusCount?: number
}

let messageSeq = 0
const nextId = () => `m${++messageSeq}`

function isAbort(e: unknown): boolean {
  return e instanceof DOMException && e.name === 'AbortError'
}

/**
 * Historie pro server: jen role a text, bez nástrojů a návrhů, prázdné odpovědi (chyby) pryč.
 * Server vyžaduje střídání rolí (zprávu uživatele bez odpovědi nahradí další zpráva uživatele),
 * odpovědi asistenta se zkracují na limit schématu a historie začíná zprávou uživatele.
 */
function toHistory(list: ChatUiMessage[]): ChatMessage[] {
  const history: ChatMessage[] = []
  for (const m of list) {
    if (m.text.trim().length === 0) continue
    const next: ChatMessage =
      m.role === 'assistant'
        ? { role: 'assistant', content: m.text.slice(0, CHAT_ASSISTANT_MAX_LENGTH) }
        : { role: 'user', content: m.text }
    if (history.length > 0 && history[history.length - 1]!.role === next.role) {
      history[history.length - 1] = next
    } else {
      history.push(next)
    }
  }
  const recent = history.slice(-CHAT_MAX_MESSAGES)
  while (recent.length > 1 && recent[0]!.role === 'assistant') recent.shift()
  return recent
}

export const useBuddyStore = defineStore('buddy', () => {
  /** Čekající návrhy (status proposed). */
  const proposals = ref<AiProposalItem[]>([])
  /** Návrhy rozhodnuté v této relaci (nejnovější první), aby šly vrátit zpět. */
  const recentDecided = ref<AiProposalItem[]>([])
  const status = ref<BuddyStatus>('idle')
  const messages = ref<ChatUiMessage[]>([])
  const sending = ref(false)
  const usage = ref<AiUsageResponse | null>(null)
  const usageStatus = ref<BuddyStatus>('idle')

  let epoch = 0
  let controller: AbortController | null = null

  /** Načte čekající návrhy. `silent`: data už jsou vidět, chyba obrazovku nerozbije. */
  async function load(silent = false): Promise<void> {
    const seq = epoch
    if (!silent || status.value === 'idle') status.value = 'loading'
    try {
      const res = await getAiProposals()
      if (seq !== epoch) return
      proposals.value = res.data
      status.value = 'ready'
    } catch {
      if (seq !== epoch) return
      if (!silent || status.value === 'loading') status.value = 'error'
    }
  }

  function applyEvent(msg: ChatUiMessage, event: ChatEvent): void {
    switch (event.type) {
      case 'text':
        msg.text += event.delta
        break
      case 'tool': {
        const open =
          event.status === 'running'
            ? undefined
            : [...msg.tools].reverse().find((t) => t.name === event.name && t.status === 'running')
        if (open) open.status = event.status
        else msg.tools.push({ name: event.name, status: event.status })
        break
      }
      case 'proposal':
        msg.proposals.push(event.proposal)
        if (!proposals.value.some((p) => p.id === event.proposal.id)) {
          proposals.value.unshift(event.proposal)
        }
        break
      case 'done':
        if (event.text) msg.text = event.text
        msg.streaming = false
        break
      case 'error':
        msg.error = event.code === 'rate_limited' ? 'buddy.errors.limit' : event.message
        msg.streaming = false
        break
      default:
        break
    }
  }

  /** Pošle zprávu Buddymu a streamuje odpověď do poslední zprávy asistenta. */
  async function send(text: string): Promise<void> {
    const clean = text.trim()
    if (!clean || sending.value) return

    messages.value.push({
      id: nextId(),
      role: 'user',
      text: clean,
      tools: [],
      proposals: [],
      streaming: false,
    })
    const history = toHistory(messages.value)
    messages.value.push({
      id: nextId(),
      role: 'assistant',
      text: '',
      tools: [],
      proposals: [],
      streaming: true,
    })
    // Reaktivní proxy poslední zprávy: zápisy do ní okamžitě vidí obrazovka.
    const msg = messages.value[messages.value.length - 1]!

    const ac = new AbortController()
    controller = ac
    sending.value = true
    try {
      await streamSse<ChatEvent>(
        '/ai/chat',
        { messages: history },
        { signal: ac.signal, onEvent: (event) => applyEvent(msg, event) },
      )
      if (msg.streaming) {
        // Proud skončil bez `done` i `error`: spojení se přerušilo.
        msg.error = 'buddy.errors.interrupted'
      }
    } catch (e) {
      if (isAbort(e)) {
        // Zrušeno uživatelem: prázdnou odpověď odstranit, rozepsanou nechat.
        if (!msg.text && !msg.tools.length && !msg.proposals.length) {
          const i = messages.value.findIndex((m) => m.id === msg.id)
          if (i !== -1) messages.value.splice(i, 1)
        }
      } else if (e instanceof ApiClientError) {
        msg.error = e.code === 'rate_limited' ? 'buddy.errors.limit' : 'buddy.errors.generic'
      } else {
        msg.error = 'common.network'
      }
    } finally {
      msg.streaming = false
      if (controller === ac) {
        controller = null
        sending.value = false
      }
    }
  }

  const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

  /**
   * Přidá do chatu hlasovku (uživatelská bublina) a odpověď Buddyho se stavem zpracování,
   * pak začne sledovat stav na serveru. Stejná hlasovka podruhé (duplicate) se nepřidá.
   */
  function attachVoiceNote(input: AttachVoiceNoteInput): void {
    if (messages.value.some((m) => m.voice?.interactionId === input.interactionId)) return
    messages.value.push({
      id: nextId(),
      role: 'user',
      text: input.deviceText.trim(),
      tools: [],
      proposals: [],
      streaming: false,
      voice: {
        noteId: input.noteId,
        interactionId: input.interactionId,
        durationMs: input.durationMs,
        recordingUrl: null,
        transcribed: false,
      },
    })
    messages.value.push({
      id: nextId(),
      role: 'assistant',
      text: '',
      tools: [],
      proposals: [],
      streaming: false,
      voiceFor: input.interactionId,
      statusKey: 'buddy.voice.transcribing',
    })
    void pollNote(input.interactionId)
  }

  /**
   * Každých 1,5 s (nejvýš 60 s) se ptá na stav hlasovky a promítá ho do bublin. Stav `failed` se bere
   * jako konečný až po VOICE_FAILED_GRACE_MS: fronta mezitím může úlohu zopakovat.
   */
  async function pollNote(interactionId: string): Promise<void> {
    const seq = epoch
    let failedSince: number | null = null
    for (let tick = 0; tick < VOICE_POLL_MAX_TICKS || failedSince !== null; tick++) {
      if (tick > 0) await sleep(VOICE_POLL_MS)
      if (seq !== epoch) return
      const user = messages.value.find((m) => m.voice?.interactionId === interactionId)
      const reply = messages.value.find((m) => m.voiceFor === interactionId)
      // Konverzace se mezitím zahodila.
      if (!user?.voice || !reply) return

      let note
      try {
        note = await getAiNote(interactionId)
      } catch (e) {
        if (seq !== epoch) return
        if (e instanceof ApiClientError && e.status >= 400 && e.status < 500 && e.status !== 429) {
          reply.statusKey = undefined
          reply.error = 'buddy.voice.failed'
          return
        }
        // Výpadek spojení: zkusíme to znovu, dokud nevyprší čas.
        continue
      }
      if (seq !== epoch) return

      user.voice.recordingUrl = note.recordingUrl
      if (note.transcript) {
        user.text = note.transcript
        user.voice.transcribed = true
      }

      const state = note.processingStatus
      if (state === 'failed') {
        failedSince ??= Date.now()
        if (Date.now() - failedSince >= VOICE_FAILED_GRACE_MS) {
          reply.statusKey = undefined
          reply.error = note.processingError || 'buddy.voice.failed'
          return
        }
        continue
      }
      // Úloha se mezitím znovu rozběhla: pokračujeme normálně.
      failedSince = null
      if (state === null || state === 'done') {
        reply.proposals = note.proposals
        for (const p of note.proposals) {
          if (!proposals.value.some((x) => x.id === p.id)) proposals.value.unshift(p)
        }
        reply.statusKey = note.proposals.length ? 'buddy.voice.proposals' : 'buddy.voice.noActions'
        reply.statusCount = note.proposals.length
        return
      }
      reply.statusKey = state === 'extracting' ? 'buddy.voice.thinking' : 'buddy.voice.transcribing'
    }
    // Čas vypršel: zpracování pokračuje na serveru, výsledek bude mezi čekajícími návrhy.
    if (seq !== epoch) return
    const reply = messages.value.find((m) => m.voiceFor === interactionId)
    if (reply && reply.statusKey !== 'buddy.voice.proposals') reply.statusKey = 'buddy.voice.slow'
  }

  /** Návrh podle id z čekajících, nedávno rozhodnutých i z karet v chatu. */
  function getProposal(id: string): AiProposalItem | undefined {
    const direct =
      proposals.value.find((p) => p.id === id) ?? recentDecided.value.find((p) => p.id === id)
    if (direct) return direct
    for (const m of messages.value) {
      const found = m.proposals.find((p) => p.id === id)
      if (found) return found
    }
    return undefined
  }

  /**
   * Zapíše aktuální stav návrhu všude, kde je: karty v chatu, seznam čekajících a nedávno rozhodnutých.
   * Čekající návrh se přidá (nový ze streamu), rozhodnutý z čekajících odejde.
   */
  function applyProposal(next: AiProposalItem): void {
    for (const m of messages.value) {
      const i = m.proposals.findIndex((p) => p.id === next.id)
      if (i !== -1) m.proposals[i] = next
    }
    const pending = proposals.value.findIndex((p) => p.id === next.id)
    const decidedAt = recentDecided.value.findIndex((p) => p.id === next.id)
    if (next.status === 'proposed') {
      if (pending !== -1) proposals.value[pending] = next
      else if (decidedAt === -1) proposals.value.unshift(next)
      return
    }
    if (pending !== -1) proposals.value.splice(pending, 1)
    if (decidedAt !== -1) recentDecided.value[decidedAt] = next
    else recentDecided.value = [next, ...recentDecided.value].slice(0, RECENT_DECIDED_MAX)
  }

  /**
   * Schválení a vrácení zpět mění data jinde (slib, úkol, termín, zakázka, klient). Seznamy, které už
   * se někdy načetly, se zahodí a načtou znovu (otevřená obrazovka dostane čerstvá data); co se ještě
   * nenačetlo, se načte při otevření. Dnes se tiše obnoví, pokud je načtené.
   */
  function syncAfterDecision(p: AiProposalItem): void {
    const reload = (store: { status: string; invalidate(): void; load(): Promise<void> }) => {
      if (store.status === 'idle') return
      store.invalidate()
      void store.load()
    }
    switch (p.result?.entityType) {
      case 'promise':
        reload(usePromisesStore())
        break
      case 'task':
        reload(useTasksStore())
        break
      case 'event': {
        const calendar = useCalendarStore()
        calendar.invalidateCache()
        if (calendar.status !== 'idle') void calendar.load(true)
        break
      }
      default:
        break
    }
    reload(useClientsStore())
    reload(useJobsStore())
    void useTodayStore().refresh()
  }

  /**
   * Rozhodnutí návrhu (z této relace i ze streamu): karty a seznamy, cache entit a Dnes.
   * Stejné rozhodnutí podruhé (ozvěna ze streamu po vlastním kliknutí) data znovu nenačítá.
   */
  function applyDecision(p: AiProposalItem): void {
    const known = recentDecided.value.find((x) => x.id === p.id)
    const repeated = known?.status === p.status && known.decidedAt === p.decidedAt
    applyProposal(p)
    if (!repeated) syncAfterDecision(p)
  }

  function errorResult(e: unknown, action: 'approve' | 'reject' | 'undo'): ProposalActionResult {
    if (!(e instanceof ApiClientError)) return { ok: false, errorKey: 'common.network' }
    if (e.status === 422 && isNeedsInput(e.details)) {
      return { ok: false, errorKey: 'proposal.errors.needsInput', needsInput: e.details }
    }
    if (e.status === 429 || e.code === 'rate_limited') {
      return { ok: false, errorKey: 'buddy.errors.limit' }
    }
    if (e.status === 409) {
      // Podobný klient už ve firmě je (ne „návrh je vyřízený“): rozhoduje uživatel.
      const duplicate = action === 'approve' ? conflictCandidates(e) : null
      if (duplicate) return { ok: false, errorKey: 'proposal.errors.duplicate', duplicate }
      if (action === 'undo') return { ok: false, errorKey: 'proposal.errors.conflict' }
      // Návrh mezitím vyřídil někdo jiný: načteme aktuální stav.
      void load(true)
      return { ok: false, errorKey: 'proposal.errors.alreadyDecided' }
    }
    if (e.status === 404) return { ok: false, errorKey: 'proposal.errors.notFound' }
    return { ok: false, errorKey: 'proposal.errors.generic' }
  }

  async function decide(
    action: 'approve' | 'reject' | 'undo',
    call: () => Promise<{ proposal: AiProposalItem }>,
  ): Promise<ProposalActionResult> {
    const seq = epoch
    try {
      const res = await call()
      if (seq === epoch) applyDecision(res.proposal)
      return { ok: true, proposal: res.proposal }
    } catch (e) {
      return errorResult(e, action)
    }
  }

  /** Schválí návrh (s `input` po úpravě ve formuláři). */
  function approve(id: string, input?: Record<string, unknown>): Promise<ProposalActionResult> {
    return decide('approve', () => approveProposal(id, input))
  }

  function reject(id: string, reason?: string): Promise<ProposalActionResult> {
    return decide('reject', () => rejectProposal(id, reason))
  }

  function undo(id: string): Promise<ProposalActionResult> {
    return decide('undo', () => undoProposal(id))
  }

  /** Přeruší probíhající odpověď. */
  function cancel(): void {
    controller?.abort()
  }

  /** Nová konverzace: zahodí historii (a přeruší probíhající odpověď). */
  function reset(): void {
    cancel()
    controller = null
    sending.value = false
    messages.value = []
  }

  async function loadUsage(): Promise<void> {
    const seq = epoch
    usageStatus.value = 'loading'
    try {
      const res = await getAiUsage()
      if (seq !== epoch) return
      usage.value = res
      usageStatus.value = 'ready'
    } catch {
      if (seq !== epoch) return
      usageStatus.value = 'error'
    }
  }

  function invalidate(): void {
    epoch++
    controller?.abort()
    controller = null
    messages.value = []
    proposals.value = []
    recentDecided.value = []
    status.value = 'idle'
    sending.value = false
    usage.value = null
    usageStatus.value = 'idle'
  }

  return {
    proposals,
    recentDecided,
    status,
    messages,
    sending,
    usage,
    usageStatus,
    load,
    send,
    attachVoiceNote,
    pollNote,
    getProposal,
    applyProposal,
    applyDecision,
    approve,
    reject,
    undo,
    cancel,
    reset,
    loadUsage,
    invalidate,
  }
})
